import { lazy, useContext, useRef } from 'react';
import { Route, Navigate, Routes, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { usePageView } from '@koob/google-tag-manager';
import { AuthContext } from 'contexts';
import Layout from 'components/Layout';
import AppProvider from 'providers/AppProvider';
import { KoobMapsProvider } from '@koob/maps';
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_MAP_ID } from './constants';
import ToConnectionRequest from './containers/ToConnectionRequest';
import ExperiencesRoutes from 'containers/Experiences/routes';
import BookingProvider from './providers/BookingProvider';
import TripDesignerConfigProvider from 'providers/TripDesignerConfigProvider';
import { NotificationsProvider } from '@koob/notifications';
import * as notificationsApi from 'api/node/notifications';
import AllotmentRoutes from './containers/Allotments/routes';
import NightRatesRoutes from './containers/NightRates/routes';
// KD-3735: pages for one organization; the group view asks which first.
import RequireActingOrganization from 'components/RequireActingOrganization';
import CacheProvider from 'providers/CacheProvider';
import LanguageProvider from 'providers/LanguageProvider';
import { KoobErrorBoundary, KoobNotFound } from '@koob/ui';
import SettingsModal from 'components/SettingsModal';
import { isSettingsPath } from 'components/settingsAreas';

const Users = lazy(() => import('containers/Users'));
const Hotels = lazy(() => import('containers/Hotels'));
const Organizations = lazy(() => import('containers/Organizations'));
const Billing = lazy(() => import('containers/Billing'));
const Bookings = lazy(() => import('containers/Bookings'));
const TripDesigner = lazy(() => import('containers/TripDesigner'));
const Trips = lazy(() => import('containers/Trips'));
const ProfileContainer = lazy(() => import('containers/Profile'));
const KoobUiShowcase = lazy(() => import('@koob/ui/src/pages/KoobUiShowcase'));
const KoobIconsGallery = lazy(
  () => import('@koob/ui/src/pages/KoobIconsGallery'),
);
const NotificationPreview = lazy(
  () => import('containers/NotificationPreview'),
);
const McpAuthorize = lazy(() => import('containers/McpAuthorize'));

const MapsWrapper = ({ children }) => {
  return (
    <KoobMapsProvider
      apiKey={GOOGLE_MAPS_API_KEY}
      mapId={GOOGLE_MAPS_MAP_ID}
      language="en"
    >
      {children}
    </KoobMapsProvider>
  );
};

const App = () => {
  const { accessToken, logout, isActiveToken } = useContext(AuthContext);
  const location = useLocation();
  usePageView(location);
  const navigate = useNavigate();

  // Settings pages open in a modal over the last other page, which stays
  // rendered beneath. A settings URL opened directly has none: nothing shows
  // beneath (a page mounting there would rewrite the URL to its own and
  // close the modal), and closing goes to the hotels.
  const lastPage = useRef(null);
  const inSettings = isSettingsPath(location.pathname);
  if (!inSettings) lastPage.current = location;
  const page = lastPage.current;

  if (Boolean(!accessToken)) {
    // Carry the target so Login can come back (deep links, MCP consent).
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  if (!isActiveToken) {
    logout();
  }

  return (
    <AppProvider>
      <BookingProvider>
        <LanguageProvider>
          <TripDesignerConfigProvider>
            <NotificationsProvider api={notificationsApi}>
              <CacheProvider>
                <Layout>
                  <MapsWrapper>
                    {page && (
                      <KoobErrorBoundary resetKey={page.pathname}>
                        <Routes location={page}>
                          <Route
                            path="organizations/*"
                            element={<Organizations />}
                          />
                          <Route path="users/*" element={<Users />} />
                          <Route path="hotels/*" element={<Hotels />} />
                          <Route
                            path="experiences/*"
                            element={<ExperiencesRoutes />}
                          />
                          <Route path="billing/*" element={<Billing />} />
                          <Route path="bookings/*" element={<Bookings />} />
                          <Route
                            path="trip-designer/*"
                            element={<TripDesigner />}
                          />
                          <Route path="trips/*" element={<Trips />} />
                          <Route
                            path="to-connection-request/*"
                            element={
                              <RequireActingOrganization>
                                <ToConnectionRequest />
                              </RequireActingOrganization>
                            }
                          />
                          <Route
                            path="night-rates/*"
                            element={
                              <RequireActingOrganization>
                                <NightRatesRoutes />
                              </RequireActingOrganization>
                            }
                          />
                          <Route
                            path="allotments/*"
                            element={
                              <RequireActingOrganization>
                                <AllotmentRoutes />
                              </RequireActingOrganization>
                            }
                          />
                          <Route
                            path="mcp/authorize"
                            element={<McpAuthorize />}
                          />
                          <Route path="user/*" element={<ProfileContainer />} />
                          <Route path="ui" element={<KoobUiShowcase />} />
                          <Route
                            path="ui/icons"
                            element={<KoobIconsGallery />}
                          />
                          <Route
                            path="ui/notifications"
                            element={<NotificationPreview />}
                          />
                          <Route index element={<Navigate to="hotels" />} />
                          <Route path="*" element={<KoobNotFound />} />
                        </Routes>
                      </KoobErrorBoundary>
                    )}
                    <SettingsModal
                      open={inSettings}
                      onClose={() =>
                        navigate(page ?? '/hotels/catalog', {
                          state: page?.state,
                        })
                      }
                    />
                  </MapsWrapper>
                </Layout>
              </CacheProvider>
            </NotificationsProvider>
          </TripDesignerConfigProvider>
        </LanguageProvider>
      </BookingProvider>
    </AppProvider>
  );
};

export default App;
